"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
  ProductSortDropdown,
  ProductsFilter,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function ShopGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    isRefetching,
  } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} data-index={i} className="flex flex-col gap-4">
            <div className="aspect-[4/5] w-full animate-pulse rounded-lg bg-muted/20" />
            <div className="h-6 w-3/4 animate-pulse rounded bg-muted/20" />
            <div className="h-5 w-1/4 animate-pulse rounded bg-muted/20" />
            <div className="mt-2 h-10 w-full animate-pulse rounded-lg bg-muted/20" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="mb-4 text-muted-foreground font-default">{error}</p>
        <Button onClick={refetch} variant="outline" className="rounded-lg border-border font-default">
          Try Again
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h3 className="font-default text-2xl font-semibold text-foreground">
          No wellness essentials found
        </h3>
        <p className="mt-2 text-muted-foreground font-default">
          Try adjusting your filters or search criteria.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => (
          <div key={product.id} data-index={i} className="group flex flex-col">
            <Link
              to={product.link}
              className="relative mb-4 block aspect-[4/5] overflow-hidden rounded-lg bg-muted/10 shadow-sm transition-shadow duration-300 hover:shadow-md"
            >
              {product.featured_image_url ? (
                <img
                  src={product.featured_image_url}
                  alt={product.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-muted/20 text-sm text-muted-foreground font-default">
                  Image Coming Soon
                </div>
              )}
              {}
              <div className="absolute inset-0 flex items-end justify-center bg-black/10 pb-6 opacity-0 transition-opacity duration-300 group-hover:opacity-100 backdrop-blur-[2px]">
                <Button
                  variant="secondary"
                  className="translate-y-4 transition-transform duration-300 group-hover:translate-y-0 rounded-lg font-default bg-background/90 text-foreground hover:bg-background"
                >
                  View Details
                </Button>
              </div>
            </Link>
            
            <div className="flex flex-grow flex-col">
              <Link to={product.link}>
                <h3 className="font-default text-lg font-medium text-foreground transition-colors hover:text-primary">
                  {product.title}
                </h3>
              </Link>
              
              {product.price !== undefined && (
                <p className="mt-1 font-default text-muted-foreground">
                  ${product.price.toFixed(2)}
                </p>
              )}
              
              <div className="mt-auto pt-4">
                <AddToCartButton
                  productId={product.id}
                  className="w-full"
                  buttonClassName="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-default tracking-wider uppercase text-xs py-3"
                  text="Add to Cart"
                  showQuantity={false}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {}
      {(hasNext || hasPrev) && (
        <div className="flex items-center justify-center gap-4 border-t border-border pt-8">
          <Button
            variant="outline"
            onClick={prevPage}
            disabled={!hasPrev || isRefetching}
            className="rounded-lg border-border text-foreground hover:bg-accent hover:text-accent-foreground font-default"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={nextPage}
            disabled={!hasNext || isRefetching}
            className="rounded-lg border-border text-foreground hover:bg-accent hover:text-accent-foreground font-default"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ShopContent() {
  
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : {};
  const wpQuery = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="222568"
      id="shop-content"
      className="bg-background text-foreground px-4 py-24 sm:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <EcommerceProductsProvider wp_query={wpQuery}>
          <div className="flex flex-col gap-12 lg:flex-row">
            {}
            <aside className="hidden w-1/4 shrink-0 lg:block">
              <div className="sticky top-24 space-y-8">
                <div>
                  <h2 className="mb-6 font-default text-xl font-semibold text-foreground tracking-tight">
                    Refine Selection
                  </h2>
                  <ProductsFilter />
                </div>
              </div>
            </aside>

            {}
            <div className="w-full lg:w-3/4">
              <div className="mb-8 flex flex-col items-start justify-between gap-4 border-b border-border pb-6 sm:flex-row sm:items-center">
                <h1 className="font-default text-4xl font-semibold text-foreground tracking-tighter">
                  The Protocol Shop
                </h1>
                <div className="w-full sm:w-auto">
                  <ProductSortDropdown label="Sort by:" />
                </div>
              </div>
              
              <ShopGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}